import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { XIcon } from '../components/icons';
import { Button, CopyButton, ErrorText, cx } from '../components/ui';
import { track } from '../lib/amplitude';
import { embedCode } from '../lib/embed';
import { embedUrl, shareUrl } from '../lib/env';
import { PRIVACY_OPTIONS } from '../lib/privacy';
import type { VideoSettings } from '../lib/types';
import { qk } from '../queries/keys';
import { fetchVideoDefaults } from '../queries/videoDefaults';
import { PrivacyControl } from './PrivacyControl';
import { useSaveVideoSettings } from './useSaveVideoSettings';

/**
 * The share window: every way to hand a recording to somebody, in one place.
 *
 * The link comes first, because that is what almost everyone came for, with
 * who it opens for right under it; the owner can change that here without
 * going to Settings, since the moment of sending is when it matters. Then
 * the places a link gets sent, and then the embed code for putting the
 * player itself on a page of their own.
 *
 * Available to a viewer too, not just the owner: anyone who can watch a
 * public recording can pass it on or embed it, and the embed respects the
 * same privacy as the link, so there is nothing here a viewer should not
 * have.
 */

/** Widths the fixed-size embed offers; the height follows the recording. */
const EMBED_WIDTHS = [
  { key: 'responsive', label: 'Fits its container', width: null },
  { key: 'small', label: 'Small (640 px wide)', width: 640 },
  { key: 'medium', label: 'Medium (960 px wide)', width: 960 },
  { key: 'large', label: 'Large (1280 px wide)', width: 1280 },
] as const;
type EmbedSize = (typeof EMBED_WIDTHS)[number]['key'];

export function ShareDialog({
  shareId,
  videoId,
  title,
  domain,
  width,
  height,
  settings,
  onClose,
}: {
  shareId: string;
  /** The library id, for the owner; what lets the privacy control write. */
  videoId: string | null;
  title: string;
  /** The team's own hostname, when they have one serving. */
  domain: string | null;
  width: number | null;
  height: number | null;
  /** The owner's settings. Null for everyone else, who gets the link as is. */
  settings: VideoSettings | null;
  onClose: () => void;
}) {
  const url = shareUrl(shareId, domain);
  const embedSrc = embedUrl(shareId, domain);
  const ratio = width && height && width > 0 ? height / width : 9 / 16;
  const [size, setSize] = useState<EmbedSize>('responsive');
  const chosen = EMBED_WIDTHS.find((option) => option.key === size) ?? EMBED_WIDTHS[0];
  const code = useMemo(() => embedCode(embedSrc, title, ratio, chosen.width), [embedSrc, title, ratio, chosen.width]);

  // The owner's team may have decided who watches; the control then only
  // shows the answer. Not asked for on a viewer's behalf.
  const owner = videoId !== null && settings !== null;
  const defaults = useQuery({ queryKey: qk.videoDefaults(), queryFn: fetchVideoDefaults, enabled: owner });
  const visibilityLocked = (defaults.data?.locked ?? []).includes('visibility');
  const teamName = defaults.data?.team?.name ?? null;
  const { save, error } = useSaveVideoSettings(videoId ?? '', shareId);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const sendTo = (channel: 'email' | 'x' | 'linkedin') => track('Shared To', { video_id: shareId, channel });
  const subject = encodeURIComponent(title);
  const encodedUrl = encodeURIComponent(url);
  const notPublic = settings !== null && settings.visibility !== 'public';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="share-dialog-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 px-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="relative max-h-[calc(100dvh-2rem)] w-full max-w-lg overflow-y-auto rounded-md border border-hairline bg-slate-900 p-6 shadow-raised">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3 top-3 rounded p-1 text-chalk-faint hover:text-chalk"
        >
          <XIcon size={16} />
        </button>
        <h2 id="share-dialog-title" className="text-h4 text-chalk">
          Share
        </h2>
        <p className="mt-0.5 truncate text-caption text-chalk-dim" title={title}>
          {title}
        </p>

        {/* The link */}
        <div className="mt-5 flex items-center gap-2">
          <input
            readOnly
            autoFocus
            value={url}
            aria-label="Share link"
            onFocus={(event) => event.currentTarget.select()}
            className="h-9 min-w-0 flex-1 rounded border border-hairline bg-slate-950/70 px-3 font-mono text-caption text-chalk"
          />
          <CopyButton
            value={url}
            label="Copy link"
            copiedLabel="Copied"
            variant="primary"
            onCopied={() => track('Share Link Copied', { video_id: shareId, surface: 'share_dialog' })}
          />
        </div>
        {owner && videoId ? (
          <PrivacyControl
            videoId={videoId}
            visibility={settings.visibility}
            teamName={teamName}
            disabled={visibilityLocked}
            onChange={(visibility) => save.mutate({ visibility })}
          />
        ) : (
          <p className="border-b border-hairline py-3.5 text-caption text-chalk-faint">
            {PRIVACY_OPTIONS[0].detail(null)}
          </p>
        )}
        {error ? (
          <div className="mt-2">
            <ErrorText>{error}</ErrorText>
          </div>
        ) : null}

        {/* Where links get sent */}
        <Section title="Send it">
          <div className="flex flex-wrap gap-2">
            <ShareTo href={`mailto:?subject=${subject}&body=${encodedUrl}`} onClick={() => sendTo('email')}>
              Email
            </ShareTo>
            <ShareTo href={`https://x.com/intent/post?text=${subject}&url=${encodedUrl}`} onClick={() => sendTo('x')}>
              X
            </ShareTo>
            <ShareTo
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`}
              onClick={() => sendTo('linkedin')}
            >
              LinkedIn
            </ShareTo>
          </div>
        </Section>

        {/* The player, on a page of their own */}
        <Section title="Embed" detail="Put the player itself on your own site, docs or blog.">
          <div className="flex items-center justify-between gap-3">
            <label htmlFor="embed-size" className="text-caption text-chalk-dim">
              Size
            </label>
            <select
              id="embed-size"
              value={size}
              onChange={(event) => setSize(event.target.value as EmbedSize)}
              className="h-8 rounded-xs border border-chalk/20 bg-slate-950/70 px-2 text-caption text-chalk"
            >
              {EMBED_WIDTHS.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <textarea
            readOnly
            value={code}
            rows={4}
            aria-label="Embed code"
            onFocus={(event) => event.currentTarget.select()}
            className="mt-3 w-full resize-none rounded border border-hairline bg-slate-950/70 p-3 font-mono text-micro leading-relaxed text-chalk-dim"
          />
          <div className="mt-2 flex items-center justify-between gap-3">
            <CopyButton
              value={code}
              label="Copy embed code"
              copiedLabel="Copied"
              size="sm"
              onCopied={() => track('Embed Code Copied', { video_id: shareId, size })}
            />
            <a
              href={embedSrc}
              target="_blank"
              rel="noreferrer"
              className="text-caption text-chalk-faint underline-offset-2 hover:text-chalk hover:underline"
            >
              Preview the player
            </a>
          </div>
          {notPublic ? (
            <p className="mt-3 text-caption text-chalk-faint">
              Embedded, it plays only for people who could open the link. For everyone who lands on
              your page, set who can watch to anyone with the link.
            </p>
          ) : null}
        </Section>
      </div>
    </div>
  );
}

function Section({ title, detail, children }: { title: string; detail?: string; children: ReactNode }) {
  return (
    <div className="border-b border-hairline py-4 last:border-b-0 last:pb-0">
      <p className="text-body text-chalk">{title}</p>
      {detail ? <p className="mt-0.5 text-caption text-chalk-faint">{detail}</p> : null}
      <div className={cx(detail ? 'mt-3' : 'mt-2.5')}>{children}</div>
    </div>
  );
}

/** A destination, opened in its own tab so this page stays where it was. */
function ShareTo({ href, onClick, children }: { href: string; onClick: () => void; children: ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" onClick={onClick}>
      <Button size="sm">{children}</Button>
    </a>
  );
}
